import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { AddOrEditIntegrationDrawerFooter } from '@modules/integrationsV2/components/AddOrEditIntegrationDrawerFooter';
import { AddOrEditIntegrationDrawerHeader } from '@modules/integrationsV2/components/AddOrEditIntegrationDrawerHeader';
import { IntegrationDescriptionForm } from '@modules/integrationsV2/components/IntegrationDescriptionForm';
import { IntegrationDisplayNameConfirmationDialogContent } from '@modules/integrationsV2/components/IntegrationDisplayNameConfirmationDialogContent';
import {
  useUpdateIntegration,
  useValidateIntegrationForm,
} from '@modules/integrationsV2/hooks';
import {
  getAddIntegrationDrawerHeaderTitle,
  trackIntegrationEvent,
} from '@modules/integrationsV2/utils';
import { GetAddIntegrationFormDrawerContentProps } from '@modules/integrationsV2/utils/getAddIntegrationFormDrawerContent';
import { Box, Stack } from '@mui/material';
import {
  IntegrationChannelType,
  IntegrationDirection,
  IntegrationType,
} from 'generated/graphql';
import { IntegrationNameErrorMessages } from 'modules/ui/Settings/Integrations/utils/integrationErrorMessages';
import { ChangeEvent, useState } from 'react';
import ErrorCode from 'v2/core/errors/codes';
import { isErrorCode } from 'v2/core/errors/errors';
import useCreateIntegrationEdgeServer from 'v2/core/repository/edge-server/integrations';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { useGetUser } from 'v2/core/services/user';
import { SnowflakeFormTextInputs } from './SnowflakeFormTextInputs';

export interface SnowflakeFormData {
  accountName: string;
  database: string;
  schema: string;
  warehouse: string;
  table: {
    errorMessage: null | string;
    value: string;
  };
  user: string;
  role: string;
  displayName: {
    errorMessage: null | string;
    value: string;
  };
}

const initialState: SnowflakeFormData = {
  accountName: '',
  database: '',
  schema: '',
  warehouse: '',
  table: {
    errorMessage: null,
    value: '',
  },
  user: '',
  role: '',
  displayName: {
    errorMessage: null,
    value: '',
  },
};

export const SnowflakeFormDrawerContent = ({
  integrationType,
  integrationChannel,
  onDrawerClose,
  integrationID,
}: GetAddIntegrationFormDrawerContentProps) => {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();

  const { openModal } = useMuiModalContext();

  const { showErrorNotification } = useNotificationContext();

  const [formData, setFormData] = useState<SnowflakeFormData>(initialState);
  const [descriptionInput, setDescriptionInput] = useState('');

  const { createIntegration, status: createIntegrationStatus } =
    useCreateIntegrationEdgeServer({
      tenantID,
      direction: IntegrationDirection.Inbound,
      channel: IntegrationChannelType.Snowflake,
      integrationType,
      createdByUser: userID,
      inputData: {
        snowflake: {
          accountName: formData.accountName,
          database: formData.database,
          schema: formData.schema,
          warehouse: formData.warehouse,
          table: formData.table.value,
          user: formData.user,
          role: formData.role,
        },
      },
      description: descriptionInput.trim(),
      displayName: formData.displayName.value,
      onError: (error: Error) => {
        if (isErrorCode(error, ErrorCode.DuplicateRecord)) {
          setFormData({
            ...formData,
            table: {
              value: formData.table.value,
              errorMessage: IntegrationNameErrorMessages.DUPLICATE_NAME,
            },
          });
          return;
        }
        showErrorNotification({
          contentComponent: `Something went wrong${
            error.message ? `: ${error.message}` : '!'
          }`,
          props: {},
        });
      },
      onCompleted: () => {
        onDrawerClose();
        trackIntegrationEvent({
          eventName: 'Integration Added',
          integrationChannelType: IntegrationChannelType.Snowflake,
        });
      },
    });

  const [updateIntegration, { status: updateIntegrationStatus }] =
    useUpdateIntegration({
      action: 'reauth',
      channelType: IntegrationChannelType.Snowflake,
      integrationType,
      onCompleted: () => {
        onDrawerClose();
      },
    });

  const isReauthorizeForm = !!integrationID;

  const [
    submitForm,
    {
      isDisabled,
      isDescriptionRequired,
      isValidatingDescription,
      descriptionValidForQuestions,
      validatedDescriptionMessage,
      isPreValidatedDescription,
    },
  ] = useValidateIntegrationForm({
    shouldValidateDescription: !isReauthorizeForm,
    channelType: IntegrationChannelType.Snowflake,
    integrationType,
    hasValidFormFields: isReauthorizeForm
      ? !!formData.warehouse
      : !!formData.accountName &&
        !!formData.database &&
        !!formData.schema &&
        !!formData.warehouse &&
        !!formData.table.value &&
        !formData.table.errorMessage,
    descriptionInput,
    currentDescription: '',
    onValidated: async () => {
      if (integrationID) {
        await updateIntegration({
          integrationID,
          isActive: true,
          updateAuthData: {
            snowflake: {
              warehouse: formData.warehouse,
              user: formData.user,
              role: formData.role,
            },
          },
        });
      } else {
        await createIntegration();
        trackIntegrationEvent({
          eventName: 'Integration Form Connect CTA Clicked',
          integrationChannelType: IntegrationChannelType.Snowflake,
        });
      }
    },
  });

  const loading =
    IsLoading(createIntegrationStatus) || IsLoading(updateIntegrationStatus);

  const handleTextInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (name === 'table' || name === 'displayName') {
      if (value.length > 100) {
        return setFormData({
          ...formData,
          [name]: {
            value,
            errorMessage: IntegrationNameErrorMessages.CHARACTER_LIMIT_EXCEEDED,
          },
        });
      }

      if (value.includes('-')) {
        return setFormData({
          ...formData,
          [name]: {
            value,
            errorMessage: IntegrationNameErrorMessages.DASH_UNSUPPORTED,
          },
        });
      }
      return setFormData({
        ...formData,
        [name]: {
          value,
          errorMessage: null,
        },
      });
    }
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const onSubmit = () => {
    if (integrationType !== IntegrationType.Feedback || isReauthorizeForm) {
      submitForm();
      return;
    }

    openModal({
      contentComponent: () => (
        <IntegrationDisplayNameConfirmationDialogContent
          displayName={formData.displayName.value}
          onConfirm={submitForm}
        />
      ),
      modalProps: {
        maxWidth: 'md',
        fullWidth: false,
        PaperProps: {
          sx: {
            width: '700px',
          },
        },
      },
    });
  };

  return (
    <Stack height="100%" justifyContent="space-between">
      <Box>
        <AddOrEditIntegrationDrawerHeader
          integrationChannel={integrationChannel}
          integrationType={integrationType}
          title={getAddIntegrationDrawerHeaderTitle(
            integrationChannel,
            integrationType,
          )}
          isReauthorizeForm={isReauthorizeForm}
        />
        <Stack p={3} spacing={4}>
          <Stack spacing={2}>
            <SnowflakeFormTextInputs
              formData={formData}
              handleTextInputChange={handleTextInputChange}
              isLoading={loading}
              integrationType={integrationType}
              isReauthorizeForm={isReauthorizeForm}
            />
          </Stack>
          {!isReauthorizeForm && (
            <IntegrationDescriptionForm
              value={descriptionInput}
              onChange={setDescriptionInput}
              isRequired={isDescriptionRequired}
              validatedDescriptionMessage={validatedDescriptionMessage}
              isValidationInProgress={isValidatingDescription}
              descriptionValidForQuestions={descriptionValidForQuestions}
              isPreValidatedDescription={isPreValidatedDescription}
              integrationType={integrationType}
            />
          )}
        </Stack>
      </Box>
      <AddOrEditIntegrationDrawerFooter
        onCancel={onDrawerClose}
        onSubmit={onSubmit}
        buttonText={isReauthorizeForm ? 'Reauthorize' : 'Integrate'}
        isDisabled={isDisabled}
        isLoading={loading}
      />
    </Stack>
  );
};
